import { MEAL_PLAN_NEW_USER_PROMO_CODES } from 'common/Constants';
import { LoginModalDismissElements } from 'common/events/ClientEvents';
import { DEFAULT_NEW_USER_CODE, NEW_USER_CODE_FOR_HUBLESS_REGIONS } from 'common/PromoCodeUtilities';
import { StorageKeys } from 'common/storage/constants';
import React, { useContext, useReducer } from 'react';
import { useHistory } from 'src/shared/hooks/useHistory';
import styled from 'styled-components';
import { match, P } from 'ts-pattern';

import { CELEBRITY_EVENT_PROMO_BANNER } from 'src/celebrity-event/Content';
import { CelebrityEventContext } from 'src/celebrity-event/context/CelebrityEventProvider';
import {
  AuthResult,
  CurrentUserFieldsFragment,
  OAuthLoginType,
  OAuthMissingFieldsResult,
  PromoCodeType,
} from 'src/gqlReactTypings.generated.d';
import { usePromotionalsHook } from 'src/promotionals/usePromotionalsHook';
import { BrandLogo } from 'src/shared/brand/BrandLogo';
import { Header2 } from 'src/shared/design-system/Headers';
import { ArrowSoftLeftV2, Close } from 'src/shared/design-system/Icon';
import { useLocation } from 'src/shared/hooks/useLocation';
import { Routes } from 'src/shared/Routes';
import { useLocalStorage } from 'src/shared/storage/Storage';
import { getSluggedPath, locationInRoutes } from 'src/shared/utils/RouteUtilities';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { Colors, Font, Spacing, ZIndex } from 'web-common/src/shared/styles';
import { AuthSecondaryButton } from './AuthSecondaryButton';
import { LoginModalMealPlanHeader } from './LoginModalMealPlanHeader';
import { LogInOrSignUpModalEntryForm } from './LoginOrSignUpModalEntryForm';
import { LoginWithEmail } from './LoginWithEmail';
import { MealPlanPromoBanner } from './MealPlanPromoBanner';
import { OAuthAdditionalFieldsForm } from './OAuthAdditionalFieldsForm';
import { OAuthMissingFieldsForm } from './OAuthMissingFieldsForm';
import { SignUpGateEntryForm } from './SignUpGateEntryForm';
import { SignUpGateLoginForm } from './SignUpGateLoginForm';
import { GateScreen, SignUpGateScreen } from './SignUpGateScreen';
import { SignUpGateSignUpForm } from './SignUpGateSignUpForm';
import { SignUpWithEmail } from './SignUpWithEmail';
import { useShowLoginOrSignUpModal } from './useShowLoginOrSignUpModal';

const HeaderContainer = styled.div<{ stickyHeader?: boolean }>`
  position: ${ifProp('stickyHeader', 'sticky')};
  top: 0;
  z-index: ${ZIndex.ModalContent + 1};
  background: ${Colors.WHITE};
`;

const FullScreenHeader = styled(Header2)`
  font-family: ${Font.Family.SANS};
  font-size: ${Font.Size.XL};
  line-height: ${Font.LineHeight.L};
  margin-top: ${Spacing.SINGLE_HALF};
`;

const ModalHeader = styled.span`
  text-align: center;
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.BOLD};
  font-size: ${Font.Size.S};
`;

enum FormType {
  ENTRY_POINT,
  REGISTER_WITH_EMAIL,
  LOGIN_WITH_EMAIL,
  OAUTH_MISSING_FIELDS,
  OAUTH_ADDITIONAL_FIELDS,
}

/** Keyed on FormType so a new form state fails the build until it picks a gate screen. */
const GATE_SCREEN_BY_FORM_TYPE: Record<FormType, GateScreen> = {
  [FormType.ENTRY_POINT]: GateScreen.ENTRY,
  [FormType.REGISTER_WITH_EMAIL]: GateScreen.SIGN_UP,
  [FormType.LOGIN_WITH_EMAIL]: GateScreen.LOGIN,
  [FormType.OAUTH_MISSING_FIELDS]: GateScreen.POST_SSO,
  [FormType.OAUTH_ADDITIONAL_FIELDS]: GateScreen.POST_SSO,
};

type OAuthMissingFieldsState = {
  formType: FormType.OAUTH_MISSING_FIELDS;
  oAuthType: OAuthLoginType;
  token: string;
  missingFields: OAuthMissingFieldsResult;
};

type OAuthAdditionalFieldsState = {
  formType: FormType.OAUTH_ADDITIONAL_FIELDS;
  oAuthType: OAuthLoginType;
  user: AuthResult;
  token: string;
  isExistingUser: boolean;
  onOAuthSuccess: (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => void;
};

type EmailFormState = {
  formType: FormType.ENTRY_POINT | FormType.LOGIN_WITH_EMAIL | FormType.REGISTER_WITH_EMAIL;
  email: string;
  errorMessage?: string;
};

type LoginOrSignUpModalState = OAuthMissingFieldsState | OAuthAdditionalFieldsState | EmailFormState;

interface LoginOrSignUpModalProps {
  showClose?: boolean;
  onCloseButtonClicked?: (LoginModalDismissElements) => void;
  fullScreenView?: boolean;
  /** Separate from `fullScreenView`, which LoginPage sets for mobile /login and /register. */
  showSignUpGate?: boolean;
  stickyHeader?: boolean;
  reloadOnLogin?: boolean;
}

enum ModalActionTypes {
  SET_EMAIL,
  CONTINUE_WITH_EMAIL,
  SWITCH_TO_MISSING_FIELDS_STATE,
  SWITCH_TO_ENTRY,
  SWITCH_TO_ADDITIONAL_FIELDS_STATE,
}

type ModalAction =
  | {
      type: ModalActionTypes.SET_EMAIL;
      email: string;
    }
  | {
      type: ModalActionTypes.CONTINUE_WITH_EMAIL;
      email: string;
      errorMessage?: string;
      isExistingUser: boolean;
    }
  | {
      type: ModalActionTypes.SWITCH_TO_MISSING_FIELDS_STATE;
      oAuthType: OAuthLoginType;
      token: string;
      missingFields: OAuthMissingFieldsResult;
    }
  | {
      type: ModalActionTypes.SWITCH_TO_ADDITIONAL_FIELDS_STATE;
      oAuthType: OAuthLoginType;
      user: AuthResult;
      token: string;
      isExistingUser: boolean;
      onOAuthSuccess: (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => void;
    }
  | {
      type: ModalActionTypes.SWITCH_TO_ENTRY;
    };

const isEmailState = (state: LoginOrSignUpModalState): state is EmailFormState =>
  [FormType.ENTRY_POINT, FormType.LOGIN_WITH_EMAIL, FormType.REGISTER_WITH_EMAIL].includes(state.formType);

const stateReducer = (state: LoginOrSignUpModalState, action: ModalAction): LoginOrSignUpModalState => {
  switch (action.type) {
    case ModalActionTypes.SET_EMAIL: {
      if (isEmailState(state)) {
        return { ...state, email: action.email };
      }
      return state;
    }
    case ModalActionTypes.CONTINUE_WITH_EMAIL: {
      const formType = action.isExistingUser ? FormType.LOGIN_WITH_EMAIL : FormType.REGISTER_WITH_EMAIL;
      return { formType, email: action.email, errorMessage: action.errorMessage };
    }
    case ModalActionTypes.SWITCH_TO_MISSING_FIELDS_STATE: {
      const { oAuthType, token, missingFields } = action;
      return { formType: FormType.OAUTH_MISSING_FIELDS, oAuthType, token, missingFields };
    }
    case ModalActionTypes.SWITCH_TO_ADDITIONAL_FIELDS_STATE: {
      const { oAuthType, onOAuthSuccess, user, token, isExistingUser } = action;
      return { formType: FormType.OAUTH_ADDITIONAL_FIELDS, oAuthType, onOAuthSuccess, user, token, isExistingUser };
    }
    case ModalActionTypes.SWITCH_TO_ENTRY: {
      const email = isEmailState(state) ? state.email : '';
      return { formType: FormType.ENTRY_POINT, email };
    }
    default:
      return state;
  }
};

export const LoginOrSignUpModalContent: React.FC<LoginOrSignUpModalProps> = ({
  showClose,
  onCloseButtonClicked,
  fullScreenView,
  showSignUpGate,
  stickyHeader,
}) => {
  const [state, dispatch] = useReducer(stateReducer, { formType: FormType.ENTRY_POINT, email: '' });

  const [, setStorageKey] = useLocalStorage(StorageKeys.LOGIN_ON_EXPLORE_MODAL, false);
  const {
    signUpPromoCode,
    entryHeader,
    requireZipCode,
    signUpEntryPoint,
    preventReloadOnLogin,
    onSuccessfulLogin,
    signUpCTA,
    continueCTA,
    loginCTA,
    hideBanner,
    hideGuestOption,
    showMealPlanHeader,
  } = useShowLoginOrSignUpModal();
  const { setCode, revalidatePromoCodes } = usePromotionalsHook();
  const history = useHistory();
  const location = useLocation();

  const celebrityEventContext = useContext(CelebrityEventContext);

  const onMealPlanPage = locationInRoutes(history.location, [
    Routes.CONSUMER_MEAL_PLANS_CHECKOUT,
    Routes.CONSUMER_MEAL_PLANS,
  ]);

  const promoCodesWithoutModalBanner = [DEFAULT_NEW_USER_CODE, NEW_USER_CODE_FOR_HUBLESS_REGIONS];
  const promoCodeImage = match({
    signUpPromoCode,
    celebrityEventType: celebrityEventContext.celebrityEventType,
    onMealPlanPage,
  })
    .with(
      {
        signUpPromoCode: {
          code: P.when((code) => promoCodesWithoutModalBanner.includes(code)),
        },
      },
      () => null
    )
    .with(
      { celebrityEventType: P.not(P.nullish) },
      ({ celebrityEventType }) => CELEBRITY_EVENT_PROMO_BANNER[celebrityEventType]
    )
    .with({ signUpPromoCode: { promoCodeType: PromoCodeType.Referral } }, () => <MealPlanPromoBanner />)
    .with(
      {
        onMealPlanPage: true,
      },
      () => <MealPlanPromoBanner />
    )
    .with(
      {
        signUpPromoCode: { code: P.when((code) => MEAL_PLAN_NEW_USER_PROMO_CODES.includes(code)) },
      },
      () => <MealPlanPromoBanner />
    )
    .otherwise(() => null);

  const onEmailContinueSuccess = (emailToContinueWith: string, isRegisteredUser: boolean) => {
    dispatch({
      type: ModalActionTypes.CONTINUE_WITH_EMAIL,
      email: emailToContinueWith,
      isExistingUser: isRegisteredUser,
    });
  };

  const onEmailChange = (event: React.FormEvent<HTMLInputElement>) => {
    dispatch({ type: ModalActionTypes.SET_EMAIL, email: event.currentTarget.value });
  };

  const goBackToEntry = () => dispatch({ type: ModalActionTypes.SWITCH_TO_ENTRY });

  const onOAuthMissingFields = (type: OAuthLoginType, token: string, oAuthMissingFields: OAuthMissingFieldsResult) => {
    dispatch({
      type: ModalActionTypes.SWITCH_TO_MISSING_FIELDS_STATE,
      oAuthType: type,
      token,
      missingFields: oAuthMissingFields,
    });
  };

  const onOAuthAdditionalFields = (
    type: OAuthLoginType,
    user: AuthResult,
    token: string,
    isExistingUser: boolean,
    onOAuthSuccess: (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => void
  ) => {
    dispatch({
      type: ModalActionTypes.SWITCH_TO_ADDITIONAL_FIELDS_STATE,
      oAuthType: type,
      user,
      token,
      isExistingUser,
      onOAuthSuccess,
    });
  };

  // Make sure the customers don't see the login modal on explore page experiment again.
  const onSuccessfulLoginOrSignUp = async (isExistingUser: boolean, currentUser: CurrentUserFieldsFragment) => {
    setStorageKey(true);

    await revalidatePromoCodes();
    if (!isExistingUser && signUpPromoCode) setCode(signUpPromoCode);

    if (location.pathname.startsWith(Routes.CONSUMER_MEAL_PLANS_CHECKOUT) && currentUser.hasActiveMealPlan) {
      const path = getSluggedPath(Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER);
      history.push(path);
      return;
    }

    if (location.pathname.startsWith(Routes.CONSUMER_MEAL_PLANS_UPCOMING_ORDER) && !currentUser.hasActiveMealPlan) {
      const path = getSluggedPath(Routes.CONSUMER_MEAL_PLANS_PREFERENCES);
      history.push(path);
      return;
    }

    if (!preventReloadOnLogin) {
      if (!requireZipCode && !celebrityEventContext.celebrityEventType) {
        // Flush storage before reload to ensure login state is persisted
        if ((window as any).__flushStorage) {
          (window as any).__flushStorage();
        }
        window.location.reload();
      }
    } else {
      onSuccessfulLogin?.(currentUser);
    }
  };

  const SignUpForm = showSignUpGate ? SignUpGateSignUpForm : SignUpWithEmail;
  const LoginForm = showSignUpGate ? SignUpGateLoginForm : LoginWithEmail;

  const {
    header,
    formComponent,
    promoBanner,
    closeOrBackButton,
  }: {
    header: string;
    formComponent: React.ReactNode;
    promoBanner: React.ReactNode;
    closeOrBackButton: React.ReactNode;
  } = match(state)
    .with({ formType: FormType.REGISTER_WITH_EMAIL }, (registerState) => ({
      header: 'Finish signing up',
      formComponent: (
        <SignUpForm
          onEmailChange={onEmailChange}
          email={registerState.email}
          onSuccess={onSuccessfulLoginOrSignUp}
          requireZipCode={requireZipCode}
          onUserAlreadyExists={(email, errorMessage) =>
            dispatch({ type: ModalActionTypes.CONTINUE_WITH_EMAIL, email, errorMessage, isExistingUser: true })
          }
          signUpPromoCode={signUpPromoCode?.code}
          signUpEntryPoint={signUpEntryPoint}
          signupCTA={signUpCTA}
        />
      ),
      promoBanner: promoCodeImage,
      closeOrBackButton: (
        <ArrowSoftLeftV2
          className={`color-neutral-800 w-4 ${fullScreenView ? '' : 'absolute'}`}
          cursor='pointer'
          onClick={() => dispatch({ type: ModalActionTypes.SWITCH_TO_ENTRY })}
        />
      ),
    }))
    .with({ formType: FormType.LOGIN_WITH_EMAIL }, (loginState) => ({
      header: 'Log in',
      formComponent: (
        <LoginForm
          onEmailChange={onEmailChange}
          email={loginState.email}
          onSuccess={onSuccessfulLoginOrSignUp}
          goBack={() => dispatch({ type: ModalActionTypes.SWITCH_TO_ENTRY })}
          errorMessage={loginState.errorMessage}
          loginCTA={loginCTA}
        />
      ),
      promoBanner: null,
      closeOrBackButton: (
        <ArrowSoftLeftV2
          className={`color-neutral-800 w-4 ${fullScreenView ? '' : 'absolute'}`}
          cursor='pointer'
          onClick={goBackToEntry}
        />
      ),
    }))
    .with({ formType: FormType.OAUTH_MISSING_FIELDS }, (oAuthMissingFieldsState) => ({
      header: 'Finish signing up',
      formComponent: (
        <OAuthMissingFieldsForm
          type={oAuthMissingFieldsState.oAuthType}
          missingFields={oAuthMissingFieldsState.missingFields}
          token={oAuthMissingFieldsState.token}
          onSuccess={onSuccessfulLoginOrSignUp}
          requireZipCode={requireZipCode}
          signUpEntryPoint={signUpEntryPoint}
        />
      ),
      closeOrBackButton: (
        <ArrowSoftLeftV2
          className={`color-neutral-800 w-4 ${fullScreenView ? '' : 'absolute'}`}
          cursor='pointer'
          onClick={goBackToEntry}
        />
      ),
      promoBanner: promoCodeImage,
    }))
    .with({ formType: FormType.OAUTH_ADDITIONAL_FIELDS }, (oAuthAdditionalFieldsState) => ({
      header: 'COMPLETE YOUR PROFILE',
      formComponent: (
        <OAuthAdditionalFieldsForm
          firstName={oAuthAdditionalFieldsState.user.user.firstName}
          lastName={oAuthAdditionalFieldsState.user.user.lastName}
          onSuccess={onSuccessfulLoginOrSignUp}
          token={oAuthAdditionalFieldsState.token}
          type={oAuthAdditionalFieldsState.oAuthType}
          signUpEntryPoint={signUpEntryPoint}
        />
      ),
      closeOrBackButton: (
        <ArrowSoftLeftV2
          className={`color-neutral-800 w-4 ${fullScreenView ? '' : 'absolute'}`}
          cursor='pointer'
          onClick={goBackToEntry}
        />
      ),
      promoBanner: null,
      shefRosterBanner: null,
    }))
    .with({ formType: FormType.ENTRY_POINT }, (entryState) => ({
      header: entryHeader ?? 'Log in or sign up',
      formComponent: showSignUpGate ? (
        <SignUpGateEntryForm
          email={entryState.email}
          onEmailChange={onEmailChange}
          onEmailContinueSuccess={onEmailContinueSuccess}
          onOAuthMissingFields={onOAuthMissingFields}
          onOAuthAdditionalFields={onOAuthAdditionalFields}
          onOAuthSuccess={onSuccessfulLoginOrSignUp}
          requireZipCode={requireZipCode}
          signUpEntryPoint={signUpEntryPoint}
          continueCTA={continueCTA}
        />
      ) : (
        <LogInOrSignUpModalEntryForm
          email={entryState.email}
          onEmailChange={onEmailChange}
          onEmailContinueSuccess={onEmailContinueSuccess}
          onOAuthMissingFields={onOAuthMissingFields}
          onOAuthAdditionalFields={onOAuthAdditionalFields}
          onOAuthSuccess={onSuccessfulLoginOrSignUp}
          requireZipCode={requireZipCode}
          signUpEntryPoint={signUpEntryPoint}
          formCloseButton={
            showClose && !hideGuestOption && onCloseButtonClicked ? (
              <AuthSecondaryButton
                onClick={() => onCloseButtonClicked(LoginModalDismissElements.BROWSE_AS_GUEST)}
                label={'Browse as Guest'}
              />
            ) : null
          }
          continueCTA={continueCTA}
        />
      ),
      promoBanner: promoCodeImage,
      closeOrBackButton:
        showClose && onCloseButtonClicked ? (
          <Close
            className={`color-neutral-800 size-3 ${fullScreenView ? '' : 'absolute'}`}
            cursor='pointer'
            onClick={() => onCloseButtonClicked(LoginModalDismissElements.CLOSE_BUTTON)}
            data-cy='login-modal-close-btn'
          />
        ) : null,
    }))
    .otherwise(() => {
      // Should not happen
      throw new Error('Missing login modal form state');
    });

  if (showSignUpGate) {
    const screen = GATE_SCREEN_BY_FORM_TYPE[state.formType];
    return (
      <SignUpGateScreen screen={screen} onBack={() => dispatch({ type: ModalActionTypes.SWITCH_TO_ENTRY })}>
        {formComponent}
      </SignUpGateScreen>
    );
  }

  if (fullScreenView) {
    return (
      <>
        <div className='mx-6 mt-6'>
          {closeOrBackButton ?? <BrandLogo color={Colors.RADISH} />}
          <FullScreenHeader>{header}</FullScreenHeader>
        </div>
        {formComponent}
      </>
    );
  }

  return (
    <>
      {!showMealPlanHeader && (
        <HeaderContainer stickyHeader={stickyHeader}>
          <div className='flex w-full flex-col content-center justify-center p-2'>
            {closeOrBackButton}
            <ModalHeader>{header}</ModalHeader>
          </div>
          <div className='h-px w-full bg-neutral-100' />
        </HeaderContainer>
      )}
      <div className='flex w-full flex-col gap-4 p-6'>
        {!hideBanner && promoBanner && !showMealPlanHeader && (
          <div className='flex w-full justify-center'>{promoBanner}</div>
        )}
        {showMealPlanHeader && <LoginModalMealPlanHeader hideImages={state.formType !== FormType.ENTRY_POINT} />}
        {formComponent}
      </div>
    </>
  );
};
